// src/features/ClientAnalysis/ClientDetailPage.js
import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Grid,
  useTheme,
  alpha,
  Stack,
  Divider,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import { 
  ArrowBack, 
  TrendingUp, 
  ShowChart, 
  CalendarToday,
  Speed,
  People,
  BarChart,
  Timeline,
  Compare
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useI18n } from '../../i18n/i18n';
import { useClientData, CLIENT_CONFIG } from './hooks/useClientData';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer, 
  BarChart as RechartsBarChart, 
  Bar, 
  Legend,
  AreaChart,
  Area
} from 'recharts';

// --- Компоненти графіків ---
const TrendChart = ({ data, type = 'line', height = 300, showForecast = false }) => {
  const theme = useTheme();
  const { t } = useI18n();
  
  if (!data || data.length === 0) {
    return (
      <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {t('client_analysis_no_data')}
        </Typography>
      </Box>
    );
  }

  const chartData = data.map(item => ({
    date: new Date(item.date).toLocaleDateString('es-ES', { month: 'short', day: 'numeric' }),
    value: item.value,
    fullDate: item.date,
    shift: item.shift
  }));

  // Простий прогноз (лінійна регресія)
  const calculateForecast = (data, periods = 7) => {
    if (data.length < 2) return [];
    
    const values = data.map(d => d.value);
    const n = values.length;
    const xSum = values.reduce((sum, _, i) => sum + i, 0);
    const ySum = values.reduce((sum, val) => sum + val, 0);
    const xySum = values.reduce((sum, val, i) => sum + i * val, 0);
    const x2Sum = values.reduce((sum, _, i) => sum + i * i, 0);
    
    const slope = (n * xySum - xSum * ySum) / (n * x2Sum - xSum * xSum);
    const intercept = (ySum - slope * xSum) / n;
    
    const forecast = [];
    for (let i = n; i < n + periods; i++) {
      forecast.push({
        date: `+${i - n + 1}d`,
        value: Math.max(0, slope * i + intercept),
        isForecast: true
      });
    }
    
    return forecast;
  };

  const forecastData = showForecast ? calculateForecast(data) : [];
  const combinedData = [...chartData, ...forecastData];

  return (
    <ResponsiveContainer width="100%" height={height}>
      {type === 'line' ? (
        <LineChart data={combinedData}>
          <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
          <XAxis 
            dataKey="date" 
            tick={{ fontSize: 12 }}
            angle={-45}
            textAnchor="end"
            height={50}
          />
          <YAxis tick={{ fontSize: 12 }} />
          <Tooltip 
            formatter={(value) => [`${value} ${t('labels_kg')}`, t('client_analysis_weight')]}
            labelFormatter={(label, payload) => {
              if (payload && payload[0]) {
                const item = payload[0].payload;
                if (item.isForecast) {
                  return `${t('analytics_forecast_label')}: ${label}`;
                }
                return new Date(item.fullDate).toLocaleDateString('es-ES');
              }
              return label;
            }}
          />
          <Legend />
          <Line 
            type="monotone" 
            dataKey="value" 
            stroke={theme.palette.primary.main}
            strokeWidth={3}
            dot={{ fill: theme.palette.primary.main, strokeWidth: 2, r: 4 }}
            activeDot={{ r: 6, fill: theme.palette.primary.dark }}
            name={t('analytics_actual_data')}
          />
          {showForecast && forecastData.length > 0 && (
            <Line 
              type="monotone" 
              dataKey="value" 
              stroke={theme.palette.warning.main}
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={{ fill: theme.palette.warning.main, r: 3 }}
              name={t('analytics_forecast_label')}
              data={forecastData}
            />
          )}
        </LineChart>
      ) : type === 'area' ? (
        <AreaChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
          <XAxis 
            dataKey="date" 
            tick={{ fontSize: 12 }}
            angle={-45}
            textAnchor="end"
            height={50}
          />
          <YAxis tick={{ fontSize: 12 }} />
          <Tooltip 
            formatter={(value) => [`${value} ${t('labels_kg')}`, t('client_analysis_weight')]}
          />
          <Area 
            type="monotone" 
            dataKey="value" 
            stroke={theme.palette.primary.main}
            fill={alpha(theme.palette.primary.main, 0.3)}
            strokeWidth={2}
          />
        </AreaChart>
      ) : (
        <RechartsBarChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
          <XAxis 
            dataKey="date" 
            tick={{ fontSize: 12 }}
            angle={-45}
            textAnchor="end"
            height={50}
          />
          <YAxis tick={{ fontSize: 12 }} />
          <Tooltip 
            formatter={(value) => [`${value} ${t('labels_kg')}`, t('client_analysis_weight')]}
          />
          <Bar 
            dataKey="value" 
            fill={theme.palette.primary.main}
            radius={[4, 4, 0, 0]}
          />
        </RechartsBarChart>
      )}
    </ResponsiveContainer>
  );
};

const ShiftComparisonChart = ({ data, height = 250 }) => {
  const theme = useTheme();
  const { t } = useI18n();
  
  if (!data || data.length === 0) {
    return (
      <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {t('client_analysis_no_data')}
        </Typography>
      </Box>
    );
  }

  // Групуємо дані по змінах
  const shiftData = data.reduce((acc, item) => {
    if (!acc[item.shift]) {
      acc[item.shift] = { total: 0, count: 0 };
    }
    acc[item.shift].total += item.value;
    acc[item.shift].count += 1;
    return acc;
  }, {});

  const chartData = Object.entries(shiftData).map(([shift, stats]) => ({
    shift: shift === 'mañana' ? t('labels_shift_morning') : t('labels_shift_evening'),
    average: stats.total / stats.count,
    total: stats.total,
    count: stats.count
  }));

  return (
    <ResponsiveContainer width="100%" height={height}>
      <RechartsBarChart data={chartData}>
        <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
        <XAxis dataKey="shift" tick={{ fontSize: 12 }} />
        <YAxis tick={{ fontSize: 12 }} />
        <Tooltip 
          formatter={(value, name) => {
            if (name === 'average') return [`${value.toFixed(1)} ${t('labels_kg')}`, t('client_stats_avg')];
            if (name === 'total') return [`${value} ${t('labels_kg')}`, t('client_stats_total')];
            return [value, name];
          }}
        />
        <Legend />
        <Bar 
          dataKey="average" 
          fill={theme.palette.primary.main}
          name={t('client_stats_avg_shift')}
          radius={[4, 4, 0, 0]}
        />
        <Bar 
          dataKey="total" 
          fill={theme.palette.secondary.main}
          name={t('client_stats_total')}
          radius={[4, 4, 0, 0]}
        />
      </RechartsBarChart>
    </ResponsiveContainer>
  );
};

const MonthlyTrendChart = ({ data, height = 250 }) => {
  const theme = useTheme();
  const { t } = useI18n();
  
  if (!data || data.length === 0) {
    return (
      <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {t('client_analysis_no_data')}
        </Typography>
      </Box>
    );
  }

  // Групуємо дані по місяцях
  const monthlyData = data.reduce((acc, item) => {
    const date = new Date(item.date);
    const monthKey = `${date.getFullYear()}-${date.getMonth() + 1}`;
    const monthName = date.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
    
    if (!acc[monthKey]) {
      acc[monthKey] = { month: monthName, total: 0, count: 0, average: 0 };
    }
    acc[monthKey].total += item.value;
    acc[monthKey].count += 1;
    acc[monthKey].average = acc[monthKey].total / acc[monthKey].count;
    
    return acc;
  }, {});

  const chartData = Object.values(monthlyData).sort((a, b) => a.month.localeCompare(b.month));

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={chartData}>
        <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
        <XAxis 
          dataKey="month" 
          tick={{ fontSize: 12 }}
          angle={-45}
          textAnchor="end"
          height={60}
        />
        <YAxis tick={{ fontSize: 12 }} />
        <Tooltip 
          formatter={(value, name) => {
            if (name === 'total') return [`${value} ${t('labels_kg')}`, t('client_stats_total_month')];
            if (name === 'average') return [`${value.toFixed(1)} ${t('labels_kg')}`, t('client_stats_avg_day')];
            return [value, name];
          }}
        />
        <Legend />
        <Line 
          type="monotone" 
          dataKey="total" 
          stroke={theme.palette.primary.main}
          strokeWidth={3}
          name={t('client_stats_total_month')}
          dot={{ fill: theme.palette.primary.main, r: 4 }}
        />
        <Line 
          type="monotone" 
          dataKey="average" 
          stroke={theme.palette.success.main}
          strokeWidth={2}
          name={t('client_stats_avg_day')}
          dot={{ fill: theme.palette.success.main, r: 3 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

// --- Компонент картки статистики ---
const StatCard = ({ title, value, unit, subtitle, color = "primary", icon: Icon }) => {
  const theme = useTheme();
  
  return (
    <Card 
      sx={{ 
        height: '100%',
        background: `linear-gradient(135deg, ${alpha(theme.palette[color].main, 0.1)} 0%, ${alpha(theme.palette[color].light, 0.05)} 100%)`,
        border: `1px solid ${alpha(theme.palette[color].main, 0.2)}`,
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 8px 24px ${alpha(theme.palette[color].main, 0.15)}`,
        }
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 48,
              height: 48,
              borderRadius: '12px',
              background: `linear-gradient(135deg, ${theme.palette[color].main} 0%, ${theme.palette[color].dark} 100%)`,
              boxShadow: `0 4px 12px ${alpha(theme.palette[color].main, 0.3)}`
            }}
          >
            <Icon sx={{ color: 'white', fontSize: 24 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette[color].dark }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
        </Stack>
        
        <Typography variant="h3" sx={{ fontWeight: 800, color: theme.palette[color].dark, mb: 1 }}>
          {value}
          {unit && (
            <Typography component="span" variant="h5" sx={{ fontWeight: 600, color: 'text.secondary', ml: 0.5 }}>
              {unit}
            </Typography>
          )}
        </Typography>
      </CardContent>
    </Card>
  );
};

export default function ClientDetailPage() {
  const { t } = useI18n();
  const { clientKey } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  
  const [tabIndex, setTabIndex] = useState(0);
  const [chartType, setChartType] = useState('line');
  const [timeRange, setTimeRange] = useState('all');

  const { selectedClientData, loading, error } = useClientData(clientKey);
  const clientConfig = CLIENT_CONFIG[clientKey];

  // Фільтруємо дані за вибраним періодом
  const filteredData = useMemo(() => {
    if (!selectedClientData?.data) return [];
    
    const now = new Date();
    let cutoffDate = new Date(0);
    
    switch (timeRange) {
      case '30days':
        cutoffDate = new Date(now.setDate(now.getDate() - 30));
        break;
      case '90days':
        cutoffDate = new Date(now.setDate(now.getDate() - 90));
        break;
      case 'year':
        cutoffDate = new Date(now.setFullYear(now.getFullYear() - 1));
        break;
      default:
        return selectedClientData.data;
    }
    
    return selectedClientData.data.filter(item => new Date(item.date) >= cutoffDate);
  }, [selectedClientData, timeRange]);

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh',
        flexDirection: 'column',
        gap: 3
      }}>
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h6" color="text.secondary">
          {t('common_loading')}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/client-analysis')}>
          {t('common_back')}
        </Button>
      </Box>
    );
  }

  if (!clientConfig || !selectedClientData) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error" variant="h6" sx={{ mb: 2 }}>
          {t('client_analysis_client_not_found')}: {clientKey}
        </Typography>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/client-analysis')}>
          {t('common_back')}
        </Button>
      </Box>
    );
  }

  const { data, statistics } = selectedClientData;

  return (
    <Box sx={{ p: 3, maxWidth: 1400, margin: '0 auto' }}>
      {/* Заголовок та навігація */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 4 }}>
        <Button 
          startIcon={<ArrowBack />} 
          onClick={() => navigate('/client-analysis')}
          variant="outlined"
          sx={{ borderRadius: '8px' }}
        >
          {t('common_back')}
        </Button>
        
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 60,
            height: 60,
            borderRadius: '16px',
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`
          }}
        >
          <People sx={{ color: 'white', fontSize: 32 }} />
        </Box>
        
        <Box sx={{ flex: 1 }}>
          <Typography variant="h3" sx={{ fontWeight: 800, mb: 1 }}>
            {clientConfig.clientName}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip 
              label={clientConfig.unit} 
              color="primary"
              sx={{ fontWeight: 600 }}
            />
            <Chip 
              label={`${t('client_analysis_code')}: ${clientConfig.clientCode}`}
              variant="outlined"
              sx={{ fontWeight: 500 }}
            />
            <Chip 
              label={`${statistics.daysWithData} ${t('client_stats_days_unit')} ${t('client_analysis_with_data')}`}
              color="success"
              variant="outlined"
            />
          </Stack>
        </Box>
      </Stack>

      {/* Статистика */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('client_stats_total')}
            value={statistics.total.toLocaleString('es-ES')}
            unit={clientConfig.unit}
            subtitle={t('client_stats_total_desc')}
            color="primary"
            icon={TrendingUp}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('client_stats_avg')}
            value={statistics.average.toFixed(1)}
            unit={clientConfig.unit}
            subtitle={t('client_stats_avg_desc')}
            color="info"
            icon={ShowChart}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('client_stats_max')}
            value={statistics.max.toLocaleString('es-ES')}
            unit={clientConfig.unit}
            subtitle={t('client_stats_max_desc')}
            color="warning"
            icon={Speed}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('client_stats_days')}
            value={statistics.daysWithData}
            unit={t('client_stats_days_unit')}
            subtitle={t('client_stats_days_desc')}
            color="success"
            icon={CalendarToday}
          />
        </Grid>
      </Grid>

      {/* Контроли фільтрів */}
      <Card sx={{ mb: 3, p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('analytics_period')}</InputLabel>
              <Select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                label={t('analytics_period')}
              >
                <MenuItem value="all">{t('analytics_period_all')}</MenuItem>
                <MenuItem value="30days">{t('analytics_period_30days')}</MenuItem>
                <MenuItem value="90days">{t('analytics_period_90days')}</MenuItem>
                <MenuItem value="year">{t('analytics_period_year')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('analytics_chart_type')}</InputLabel>
              <Select
                value={chartType}
                onChange={(e) => setChartType(e.target.value)}
                label={t('analytics_chart_type')}
              >
                <MenuItem value="line">{t('chart_type_line')}</MenuItem>
                <MenuItem value="area">{t('chart_type_area')}</MenuItem>
                <MenuItem value="bar">{t('chart_type_bar')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Card>

      {/* Вкладки аналітики */}
      <Card sx={{ mb: 4 }}>
        <Tabs 
          value={tabIndex} 
          onChange={(_, newValue) => setTabIndex(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab icon={<Timeline />} label={t('analytics_trend_forecast')} />
          <Tab icon={<Compare />} label={t('analytics_shift_comparison')} />
          <Tab icon={<BarChart />} label={t('analytics_monthly_trends')} />
          <Tab icon={<ShowChart />} label={t('client_detail_full_history')} />
        </Tabs>
      </Card>

      {/* Контент вкладок */}
      {tabIndex === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} lg={8}>
            <Card>
              <CardContent>
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>
                  {t('analytics_trend_forecast')}
                </Typography>
                <TrendChart 
                  data={filteredData} 
                  type={chartType} 
                  height={400}
                  showForecast={true}
                />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} lg={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>
                  {t('analytics_shift_comparison')}
                </Typography>
                <ShiftComparisonChart data={filteredData} height={400} />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {tabIndex === 1 && (
        <Card>
          <CardContent>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>
              {t('analytics_shift_comparison')}
            </Typography>
            <ShiftComparisonChart data={filteredData} height={500} />
          </CardContent>
        </Card>
      )}

      {tabIndex === 2 && (
        <Card>
          <CardContent>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>
              {t('analytics_monthly_trends')}
            </Typography>
            <MonthlyTrendChart data={data} height={500} />
          </CardContent>
        </Card>
      )}

      {tabIndex === 3 && (
        <Card>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ p: 3, pb: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {t('client_detail_full_history')}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {t('client_analysis_chronology')}
              </Typography>
            </Box>
            
            <Divider />
            
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table stickyHeader size="medium">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', py: 2 }}>
                      {t('client_analysis_date')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', py: 2 }}>
                      {t('client_analysis_shift')}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.9rem', py: 2 }}>
                      {clientConfig.unit}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', py: 2 }}>
                      {t('client_analysis_operators')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredData.length > 0 ? (
                    filteredData.map((record, index) => (
                      <TableRow 
                        key={index} 
                        hover
                        sx={{ 
                          '&:last-child td': { borderBottom: 0 },
                          transition: 'background-color 0.2s ease'
                        }}
                      >
                        <TableCell sx={{ py: 2 }}>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {new Date(record.date).toLocaleDateString('es-ES')}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 2 }}>
                          <Chip 
                            label={record.shift} 
                            size="small" 
                            variant="outlined" 
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ py: 2 }}>
                          <Chip 
                            label={record.value.toLocaleString('es-ES')}
                            color="primary"
                            variant="filled"
                            sx={{ fontWeight: 600, minWidth: 80 }}
                          />
                        </TableCell>
                        <TableCell sx={{ py: 2 }}>
                          <Typography variant="body2">
                            {record.operators?.join(', ') || 'N/A'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        <Typography variant="body1">
                          {t('client_analysis_no_data')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}


